/** Коллекция приёмов.
 *
 * Экран переворачивает смысл платной подсказки: купленное объяснение не
 * исчезает вместе с партией, а остаётся здесь. Трата превращается в
 * накопление, и заходить сюда становится интересно между партиями.
 *
 * Открытые приёмы идут первыми и с счётчиком применений, неоткрытые — ниже
 * и не прячутся: список показывает, сколько ещё предстоит узнать.
 */

import { useEffect, useState } from 'react'

import { fetchTechniques } from '../api/endpoints'
import type { TechniqueCatalog } from '../api/types'
import { ProgressBar } from '../components/ui'
import { pluralOf, t } from '../i18n'
import { backButton } from '../telegram/webapp'

export interface TechniquesScreenProps {
  onExit: () => void
}

export function TechniquesScreen({ onExit }: TechniquesScreenProps) {
  const [catalog, setCatalog] = useState<TechniqueCatalog | null>(null)

  useEffect(() => backButton.show(onExit), [onExit])

  useEffect(() => {
    void fetchTechniques().then(setCatalog).catch(() => setCatalog(null))
  }, [])

  if (!catalog) {
    return (
      <div className="screen screen--center">
        <span className="hint-text">{t('app.loading')}</span>
      </div>
    )
  }

  const known = catalog.items.filter((item) => item.discovered)
  const unknown = catalog.items.filter((item) => !item.discovered)

  return (
    <div className="screen">
      <header className="topbar">
        <button type="button" className="topbar__action" onClick={onExit} aria-label={t('app.back')}>
          ‹
        </button>
        <span className="topbar__title">{t('techniques.title')}</span>
        <span className="topbar__spacer" />
      </header>

      <div className="screen__body">
        <div className="row">
          <span className="hint-text">
            {t('techniques.progress', { done: catalog.discovered, total: catalog.total })}
          </span>
          <span className="hint-text">{t('techniques.source')}</span>
        </div>
        <ProgressBar value={catalog.discovered} total={catalog.total} variant="success" />

        {known.map((item) => (
          <div className="technique" key={item.code}>
            <div className="technique__head">
              <span className="technique__title">{item.title}</span>
              <span className="technique__meta">
                {t('techniques.used', {
                  count: item.times_used,
                  word: pluralOf('plural.times', item.times_used),
                })}
              </span>
            </div>
            <p
              className="technique__body"
              style={{ margin: 0, fontSize: 'var(--size-hint)' }}
            >
              {item.summary}
            </p>
          </div>
        ))}

        {unknown.length > 0 && (
          <>
            <span className="section-label" style={{ marginTop: 'var(--gap-1)' }}>
              {t('techniques.locked')}
            </span>
            {unknown.map((item) => (
              <div className="technique technique--locked" key={item.code}>
                <div className="technique__head">
                  <span className="technique__title">{item.title}</span>
                  <span className="technique__meta">{t('techniques.willOpen')}</span>
                </div>
              </div>
            ))}
          </>
        )}

        <span className="hint-text" style={{ textAlign: 'center' }}>
          {t('techniques.forever')}
        </span>
      </div>
    </div>
  )
}
